import {
  AlertTriangle,
  Bot,
  Database,
  LoaderCircle,
  MessageSquareText,
  TableProperties,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AiChatDecisionCard } from './ai-chat-decision-card'
import { AiChatMarkdown } from './ai-chat-markdown'
import {
  AiChatCitedContent,
  AiChatSources,
  aiChatSources,
} from './ai-chat-sources'
import { ToolCallDetails } from './ai-chat-tool-call-details'
import { AiChatMessageFiles } from './files/ai-chat-message-files'
import type {
  AiChatDecisionAnswer,
  AiChatInputRequestData,
} from '@amitable/shared/ai-chat-decisions'
import type { ReactNode } from 'react'
import type { AiChatMessagesQuery } from '@/graphql/queries/aiChatMessagesQuery.gql.generated'
import { AiChatMessageRole } from '@/graphql/schema'
import { cn } from '@/shared/lib/utils'

export type LiveAiChatScope = 'workspace' | 'documentCollection'
type QueryMessage = AiChatMessagesQuery['aiChatMessages'][number]
type QueryToolCall = QueryMessage['toolCalls'][number]
/** Streamed and older stored tool calls may carry no file references. */
type LiveAiChatToolCall = Omit<QueryToolCall, 'references'> & {
  references?: QueryToolCall['references']
}
export type LiveAiChatMessage = Omit<
  QueryMessage,
  'inputRequest' | 'files' | 'toolCalls'
> & {
  inputRequest?: AiChatInputRequestData | null
  files?: QueryMessage['files']
  toolCalls: Array<LiveAiChatToolCall>
}

export const workspacePrompts = [
  'workspacePromptCreateCollection',
  'workspacePromptCollections',
  'workspacePromptCrm',
  'workspacePromptViews',
]

export const documentCollectionPrompts = [
  'collectionPromptExplain',
  'collectionPromptFilters',
  'collectionPromptChart',
  'collectionPromptSummarize',
]

export function ScopeIcon({ scope }: { scope: LiveAiChatScope }) {
  const Icon = scope === 'workspace' ? Database : TableProperties

  return (
    <span className="inline-flex size-9 items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground">
      <Icon className="size-4" aria-hidden="true" />
    </span>
  )
}

function ToneBadge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode
  tone?: 'good' | 'neutral' | 'warning'
}) {
  return (
    <span
      className={cn(
        'inline-flex rounded-full border px-2 py-0.5 text-xs font-medium',
        tone === 'good' && 'border-emerald-200 bg-emerald-50 text-emerald-700',
        tone === 'neutral' && 'border-border bg-muted text-muted-foreground',
        tone === 'warning' &&
          'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-300',
      )}
    >
      {children}
    </span>
  )
}

function ToolCallSummary({
  index,
  toolCall,
}: {
  index: number
  toolCall: LiveAiChatToolCall
}) {
  const { t } = useTranslation()
  const statusTone =
    toolCall.status.toLowerCase() === 'success' ? 'good' : 'warning'

  return (
    <li className="rounded-md border border-border bg-background p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono text-xs font-medium">
          {toolCall.toolName}
        </span>
        <div className="flex items-center gap-2">
          {['web_search', 'web_extract', 'public_data_read'].includes(
            toolCall.toolName,
          ) ? (
            <ToneBadge tone="neutral">
              {t('aiChat:sourcesFound', {
                count: aiChatSources([toolCall]).length,
              })}
            </ToneBadge>
          ) : (
            <>
              <ToneBadge tone={toolCall.recordsRead > 0 ? 'good' : 'neutral'}>
                {t('aiChat:recordsRead', { count: toolCall.recordsRead })}
              </ToneBadge>
              <ToneBadge
                tone={toolCall.recordsWritten > 0 ? 'good' : 'neutral'}
              >
                {t('aiChat:recordsWritten', { count: toolCall.recordsWritten })}
              </ToneBadge>
            </>
          )}
          <ToneBadge tone={statusTone}>{toolCall.status}</ToneBadge>
        </div>
      </div>
      <p className="mt-2 text-xs leading-5 text-muted-foreground">
        {toolCall.summary ||
          t('aiChat:toolCallFallback', { number: index + 1 })}
      </p>
      {toolCall.errorCode ? (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
          {toolCall.errorCode}
        </p>
      ) : null}
    </li>
  )
}

type DecisionActions = {
  decisionsDisabled?: boolean
  onAnswer?: (
    request: AiChatInputRequestData,
    answers: Array<AiChatDecisionAnswer>,
  ) => Promise<void>
  onCancel?: (request: AiChatInputRequestData) => Promise<void>
}

function MessageContent({
  message,
  assistant,
}: {
  message: LiveAiChatMessage
  assistant: boolean
}) {
  const questionText = message.inputRequest?.questions
    .map((question) => question.question)
    .join('\n\n')
  if (questionText === message.content) return null

  const sources = aiChatSources(message.toolCalls)

  return assistant ? (
    <div className="mt-2 break-words">
      <AiChatMarkdown content={message.content} sources={sources} />
    </div>
  ) : (
    <p className="mt-2 whitespace-pre-wrap break-words text-accent-foreground">
      <AiChatCitedContent content={message.content} sources={sources} />
    </p>
  )
}

function ChatMessageBubble({
  message,
  ...decisions
}: { message: LiveAiChatMessage } & DecisionActions) {
  const { t } = useTranslation()
  const assistant = message.role === AiChatMessageRole.Assistant

  return (
    <article
      className={cn(
        'max-w-[92%] rounded-lg border p-4 text-sm leading-6',
        assistant
          ? 'border-border bg-card text-card-foreground'
          : 'ml-auto border-primary/30 bg-accent text-accent-foreground',
      )}
    >
      <div
        className={cn(
          'flex items-center gap-2 text-xs font-medium uppercase',
          assistant ? 'text-muted-foreground' : 'text-accent-foreground/75',
        )}
      >
        {assistant ? (
          <Bot className="size-3.5" aria-hidden="true" />
        ) : (
          <MessageSquareText className="size-3.5" aria-hidden="true" />
        )}
        {assistant ? t('aiChat:roleAssistant') : t('aiChat:roleUser')}
      </div>
      <MessageContent message={message} assistant={assistant} />
      <AiChatMessageFiles message={message} />
      {message.inputRequest ? (
        <AiChatDecisionCard
          request={message.inputRequest}
          disabled={decisions.decisionsDisabled}
          onAnswer={decisions.onAnswer}
          onCancel={decisions.onCancel}
        />
      ) : null}
      <AiChatSources sources={aiChatSources(message.toolCalls)} />
      {message.toolCalls.length ? (
        <ToolCallDetails>
          {message.toolCalls.map((toolCall, index) => (
            <ToolCallSummary
              key={`${toolCall.toolName}-${index}`}
              index={index}
              toolCall={toolCall}
            />
          ))}
        </ToolCallDetails>
      ) : null}
    </article>
  )
}

export function StatusNotice({
  errorMessage,
  loading,
  scope,
}: {
  errorMessage?: string
  loading: boolean
  scope?: LiveAiChatScope
}) {
  const { t } = useTranslation()
  if (!loading && !errorMessage) {
    return (
      <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm leading-6 text-muted-foreground">
        {t(
          scope === 'workspace'
            ? 'aiChat:workspaceCapabilities'
            : 'aiChat:capabilities',
        )}
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-border bg-muted/40 p-4">
      <div className="flex items-start gap-3">
        {loading ? (
          <LoaderCircle
            className="mt-0.5 size-4 animate-spin text-muted-foreground"
            aria-hidden="true"
          />
        ) : (
          <AlertTriangle
            className="mt-0.5 size-4 text-amber-600 dark:text-amber-300"
            aria-hidden="true"
          />
        )}
        <p className="text-sm leading-6 text-muted-foreground">
          {loading ? t('aiChat:loadingContext') : errorMessage}
        </p>
      </div>
    </div>
  )
}

export function PromptChips({
  disabled,
  onPickPrompt,
  prompts,
}: {
  disabled: boolean
  onPickPrompt: (prompt: string) => void
  prompts: Array<string>
}) {
  const { t } = useTranslation('aiChat')

  return (
    <div className="flex flex-wrap gap-2">
      {prompts.map((prompt) => (
        <button
          key={prompt}
          type="button"
          className="rounded-full border border-border bg-background px-3 py-1.5 text-left text-xs font-medium text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
          disabled={disabled}
          onClick={() => onPickPrompt(t(prompt))}
        >
          {t(prompt)}
        </button>
      ))}
    </div>
  )
}

export function ChatMessages({
  loading,
  messages,
  ...decisions
}: {
  loading: boolean
  messages: Array<LiveAiChatMessage>
} & DecisionActions) {
  const { t } = useTranslation()

  return (
    <div className="space-y-4" aria-live="polite">
      {messages.map((message) => (
        <ChatMessageBubble key={message.id} message={message} {...decisions} />
      ))}
      {loading ? (
        <div className="flex items-center gap-2 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
          <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
          {t('aiChat:assistantReading')}
        </div>
      ) : null}
    </div>
  )
}
